import {
  getMomentsGeneration,
  observeMomentsReadinessIdentity,
} from '../momentsPrivateBoundary';
import {
  clearMomentsPrivateState,
  failMomentsReadiness,
  restoreMomentsReadiness,
} from '../slices/appSlice';

import { api } from './index';

import type {
  AdminAppointment,
  AdminAppointmentPage,
} from '@/features/appointments/types';
import type { BaseQueryApi } from '@reduxjs/toolkit/query';

import { appointmentContractV1 } from '@/features/appointments/contractCodec';

const MAX_MOMENTS_MEMBER_SCOPE_PAGE_REQUESTS = 100;

export type MomentsMemberScopeAppointmentsPage = AdminAppointmentPage & {
  nativeUnavailable?: boolean;
};

const emptyMomentsMemberScopeAppointmentsPage: MomentsMemberScopeAppointmentsPage =
  {
    appointments: [],
    contractVersion: 'appointment-admin-page.v1',
    nextCursor: null,
  };

const unavailableMomentsMemberScopeAppointmentsPage: MomentsMemberScopeAppointmentsPage =
  {
    ...emptyMomentsMemberScopeAppointmentsPage,
    nativeUnavailable: true,
  };

const isMomentsMemberScopeEligibilityError = (
  error: MomentsQueryError,
): boolean =>
  error.status === 'MOMENTS_NOT_READY' ||
  error.status === 'MOMENTS_BASE_URL_MISSING' ||
  error.status === 'MOMENTS_TOKEN_MISSING' ||
  error.status === 403 ||
  error.status === 404 ||
  error.status === 428;

const highestVersionAppointments = (
  appointments: AdminAppointment[],
): AdminAppointment[] => {
  const byId = new Map<string, AdminAppointment>();
  appointments.forEach((appointment) => {
    const existing = byId.get(appointment.id);
    if (!existing || appointment.version > existing.version) {
      byId.set(appointment.id, appointment);
    }
  });
  return Array.from(byId.values());
};

interface RuntimeImportMetaEnv {
  VITE_MOMENTS_BASE_URL?: string;
}

interface ApiCacheState {
  queries?: Record<string, { data?: unknown; status?: string }>;
}

interface RootLikeState {
  api?: ApiCacheState;
}

interface CurrentUserLike {
  momentsBaseUrl?: string | null;
}

export interface MomentsApiSessionPerson {
  id: string;
  email: string;
  organizationId: string;
}

export interface MomentsApiSessionCapabilities {
  memberApi: boolean;
  coachTeam: boolean;
}

export interface MomentsApiSessionReadiness {
  ready: boolean;
  provisioned: boolean;
  preconditionsMet: boolean;
  reason?: 'not_provisioned' | 'drive_connect_required' | 'unknown' | string;
  person?: MomentsApiSessionPerson;
  roles: string[];
  capabilities: MomentsApiSessionCapabilities;
}

export interface MomentsCoachMember {
  personId: string;
  email: string;
  fullName: string;
  active: boolean;
  relationshipType?: string | null;
  source?: string;
  provisioned?: boolean;
}

export interface MomentsCoachMembersResponse {
  members: MomentsCoachMember[];
}

export interface MomentsFeatureFlagSubject {
  personId: string;
  roles: string[];
}

export interface MomentsFeatureFlagsForSelfResponse {
  subject: MomentsFeatureFlagSubject;
  flags: string[];
  source: 'moments';
}

export interface MomentsFeatureFlagForSubjectResponse {
  subject: MomentsFeatureFlagSubject;
  flagKey: string;
  enabled: boolean;
  source: 'moments';
}

export interface GetMomentsMemberScopeAppointmentsArg {
  cacheScope?: string;
  memberId: string;
  readMode?: 'history';
  limit?: number;
  order?:
    | 'current_start_at_asc,appointment_id_asc'
    | 'current_start_at_desc,appointment_id_desc';
  rangeEnd?: string;
  rangeStart?: string;
}

export interface MomentsSessionLinkSummary {
  sessionId: string;
  legacySessionId?: string;
  plannedStartAt?: string;
  plannedEndAt?: string;
  /** Single organizing coach's name; only the member artifact list sends it. */
  coachName?: string;
}

export interface MomentsArtifactSource {
  type: string;
  value: string;
  label?: string | null;
}

export interface MomentsArtifactTranscriptSession {
  transcriptId: string;
  /** Session start instant (RFC3339). */
  sessionStartAt?: string | null;
  /**
   * Moments session id — the id that `/m/session/:sessionId/transcript/:date`
   * and other Moments session routes accept. Mirrors
   * `MomentsSessionLinkSummary.sessionId`.
   */
  sessionId?: string | null;
  /**
   * Rails session id for the same session, when it has one. Never usable in a
   * Moments session route.
   */
  legacySessionId?: string | null;
  sessionType?: string | null;
}

export interface MomentsSessionPrepSourceReflection {
  artifactId?: string;
  routeDateSlug?: string;
  title?: string;
}

export interface MomentsSessionPrepContext {
  targetSessionId?: string;
  targetLegacySessionId?: string;
  targetPlannedStartAt?: string | null;
  targetPlannedEndAt?: string | null;
  sourceSessionId?: string;
  sourceLegacySessionId?: string;
  sourcePlannedStartAt?: string | null;
  sourcePlannedEndAt?: string | null;
  sourceReadinessVersion?: number;
  sourceInputSetHash?: string;
  sourceUsedCoachMatch?: boolean;
  sourceReflection?: MomentsSessionPrepSourceReflection;
  [key: string]: unknown;
}

export interface MomentsArtifactDetail {
  id: string;
  ownerPersonId: string;
  ownerDisplayName?: string | null;
  subjectId?: string;
  profileSlug: string;
  title: string;
  sources: MomentsArtifactSource[];
  markdown: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  revision: number;
  transcriptSessions: MomentsArtifactTranscriptSession[];
  sessionPrep?: MomentsSessionPrepContext;
}

export type MomentsArtifactFeedbackType = 'positive' | 'negative';

export interface MomentsArtifactFeedback {
  id: string;
  feedbackType: MomentsArtifactFeedbackType;
  feedbackText?: string | null;
  createdAt: string;
}

export interface MomentsArtifactFeedbackLatestResponse {
  feedback: MomentsArtifactFeedback | null;
}

export interface MomentsArtifactFeedbackCreateResponse {
  id: string;
  personId: string;
  feedbackType: MomentsArtifactFeedbackType;
  feedbackText?: string | null;
  context?: Record<string, unknown>;
  createdAt: string;
}

export interface MomentsReflectionSourceIdentity {
  revision: number;
  readerContract: 'frontend-reflection-legacy-v1';
  anchorContract: 'reflection-keep-anchor-v1';
  scope: 'whole-block' | 'selection';
}

// Contracts §8.11: whole-item highlights on member-reflection-v2 (Plan A).
export interface MomentsReflectionV2SourceIdentity {
  anchorContract: 'reflection-v2-key-v1';
  readerContract: 'reflection-v2-item-v1';
  revision: number;
  scope: 'whole-block';
}

export interface MomentsReflectionStoredSourceIdentity extends MomentsReflectionSourceIdentity {
  blockId: string;
  startIndex: number;
  endIndex: number;
}

// Contracts §8.11 v2 notes as artifact_note.list returns them; blockId is the
// item key (`realized-0`) and revision is the artifact revision highlighted.
export interface MomentsReflectionV2StoredSourceIdentity extends MomentsReflectionV2SourceIdentity {
  blockId: string;
  startIndex: number;
  endIndex: number;
}

export interface MomentsArtifactNote {
  id: string;
  ownerPersonId: string;
  artifactId: string;
  selectedText: string;
  blockId: string;
  startIndex: number;
  endIndex: number;
  note: string;
  tags: string[];
  suggestedTag?: string | null;
  authorRole: string;
  sessionRefType?: string | null;
  sessionRefId?: string | null;
  createdAt: string;
  updatedAt: string;
  sourceIdentity?:
    | MomentsReflectionStoredSourceIdentity
    | MomentsReflectionV2StoredSourceIdentity
    | null;
}

export interface MomentsArtifactNotesResponse {
  notes: MomentsArtifactNote[];
}

export type MomentsSavedHighlightSort = 'created_at' | 'session_date';
export type MomentsSavedHighlightDirection = 'asc' | 'desc';

export interface MomentsSavedHighlightArtifactSummary {
  profileSlug: string;
  routeDateSlug: string;
  sessions: MomentsSessionLinkSummary[];
  title: string;
  updatedAt: string;
}

export interface MomentsSavedHighlight extends MomentsArtifactNote {
  artifact: MomentsSavedHighlightArtifactSummary | null;
}

export interface MomentsSavedHighlightsSearchResponse {
  items: MomentsSavedHighlight[];
  totalCount: number;
  hasMore: boolean;
  nextOffset?: number;
}

export interface SearchMomentsSavedHighlightsArg {
  // Cache isolation only; authenticated ownership is always server-derived.
  cacheSubject?: string;
  view?: 'owned-snapshots';
  noteId?: string;
  direction?: MomentsSavedHighlightDirection;
  limit?: number;
  offset?: number;
  profileSlug?: string | readonly string[];
  q?: string;
  sort?: MomentsSavedHighlightSort;
}

export interface TrackMomentsAnalyticsEventRequest {
  eventName: string;
  clientEventId: string;
  occurredAt?: string;
  properties: Record<string, unknown>;
  queryParams?: Record<string, string>;
}

export interface TrackMomentsAnalyticsEventResponse {
  queued: boolean;
}

export interface MomentsAnalyticsSessionResponse {
  analyticsToken: string;
  expiresAt: string;
  personId: string;
}

export interface MomentsMemberArtifactSummary {
  artifactId: string;
  ownerPersonId: string;
  subjectId: string;
  profileSlug: string;
  title: string;
  updatedAt: string;
  revision: number;
  sessions: MomentsSessionLinkSummary[];
  sessionPrep?: MomentsSessionPrepContext;
  markdown?: string;
}

export interface MomentsMemberArtifactsResponse {
  items: MomentsMemberArtifactSummary[];
  totalCount: number;
  hasMore: boolean;
  nextOffset?: number;
}

export interface MomentsSessionArtifactsResponse {
  session: MomentsSessionLinkSummary;
  items: MomentsMemberArtifactSummary[];
}

export interface MomentsSessionTranscriptSummary {
  attachmentId: string;
  transcriptContentId?: string;
  driveFileId: string;
  displayName?: string;
  contentStatus: string;
  contentMarkdown?: string;
  contentFetchedAt?: string;
  driveModifiedAt?: string;
  contentLength?: number;
}

export interface MomentsSessionTranscriptsResponse {
  session: MomentsSessionLinkSummary;
  items: MomentsSessionTranscriptSummary[];
}

export interface MomentsSessionReadAttachmentSummary {
  attachmentId: string;
  kind: string;
  driveFileId: string;
  displayName?: string;
  status: string;
  contentStatus: string;
}

export interface MomentsSessionReadinessSummary {
  status: string;
  version: number;
  reason: string;
  inputSetHash?: string;
}

export interface MomentsSessionReadArtifactSummary {
  artifactId: string;
  profileSlug: string;
  title: string;
  sessionIds: string[];
  /** Session start instants (RFC3339). */
  sessionStartsAt: string[];
  displaySessionId: string;
  updatedAt: string;
}

export interface MomentsSessionReadItem extends MomentsSessionLinkSummary {
  status: string;
  artifacts: MomentsSessionReadArtifactSummary[];
  attachments: MomentsSessionReadAttachmentSummary[];
  artifactGenerationReadiness?: MomentsSessionReadinessSummary;
  // Member-visible artifact profiles whose generation has finally failed.
  failedArtifactProfiles: string[];
}

export interface MomentsSessionReadResponse {
  item: MomentsSessionReadItem | null;
}

export type MomentsArtifactFeedbackSurface =
  | 'coach_reflection_detail'
  | 'member_reflection_detail'
  | 'member_session_prep_detail';

export interface CreateMomentsArtifactFeedbackArg {
  artifactId: string;
  feedbackText?: string;
  feedbackType: MomentsArtifactFeedbackType;
  surface?: MomentsArtifactFeedbackSurface;
}

export interface ListMomentsArtifactNotesArg {
  artifactId?: string;
}

export interface CreateMomentsArtifactNoteArg {
  artifactId: string;
  selectedText: string;
  blockId: string;
  startIndex: number;
  endIndex: number;
  note: string;
  tags?: string[];
  suggestedTag?: string | null;
  authorRole?: 'member' | 'coach' | string;
  sessionRefType?: string;
  sessionRefId?: string;
  sourceIdentity?:
    MomentsReflectionSourceIdentity | MomentsReflectionV2SourceIdentity;
}

export interface UpdateMomentsArtifactNoteArg {
  id: string;
  note?: string;
  tags?: string[];
  suggestedTag?: string | null;
  clearSuggestedTag?: boolean;
}

export interface DeleteMomentsArtifactNoteArg {
  id: string;
}

export interface GetMomentsArtifactArg {
  artifactId: string;
  revision?: number;
}

export interface GetMomentsArtifactFeedbackLatestArg {
  artifactId: string;
}

export type MomentsArtifactSurveyQuestionId =
  'got_you' | 'will_try' | 'stood_out';

export interface RecordMomentsArtifactSurveyAnswerArg {
  answer: string;
  artifactId: string;
  question: MomentsArtifactSurveyQuestionId;
}

// Groups survey answers in analytics: responseId is one member's answers on one
// artifact, surveyId is every member's answers to the current survey.
export interface MomentsArtifactSurveyAnswerResult {
  responseId: string;
  surveyId: string;
}

export interface MomentsArtifactSurveyStatus {
  answered: boolean;
}

export interface GetMomentsMemberArtifactsArg {
  profileSlug?: string | readonly string[];
  includeContent?: boolean;
  limit?: number;
  offset?: number;
}

export interface GetMomentsSessionScopedArg {
  sessionId?: string;
  legacySessionId?: string;
  profileSlug?: string | readonly string[];
  includeContent?: boolean;
}

export interface GetMomentsCoachSessionArtifactsArg {
  legacySessionId: string;
  memberPersonId: string;
}

export interface GetMomentsSessionTranscriptsArg {
  sessionId?: string;
  legacySessionId?: string;
  includeContent?: boolean;
}

export interface GetMomentsSessionReadByLegacyIdArg {
  legacySessionId: string;
}

export interface GetMomentsFeatureFlagForSubjectArg {
  personId: string;
  flagKey: string;
}

export interface GetMomentsFeatureFlagForLegacySubjectArg {
  externalId: string;
  flagKey: string;
}

interface MomentsApiSessionReadinessWire {
  ready?: boolean;
  provisioned?: boolean;
  preconditions_met?: boolean;
  reason?: string;
  person?: {
    id?: string;
    email?: string;
    organization_id?: string;
  } | null;
  roles?: string[] | null;
  capabilities?: {
    member_api?: boolean;
    coach_team?: boolean;
  } | null;
}

interface MomentsTokenExchangeResponse {
  access_token?: string;
  expires_at?: string;
  expires_in?: number;
}

export interface MomentsPersonWire {
  id?: string;
  primary_email?: string;
  email?: string;
  given_name?: string | null;
  family_name?: string | null;
}

export interface MomentsTeamMemberWire {
  person?: MomentsPersonWire | null;
  active?: boolean;
  relationship_type?: string | null;
  source?: string;
}

export interface MomentsCoachMembersWireResponse {
  members?: MomentsTeamMemberWire[] | null;
}

export interface MomentsSessionArtifactSummaryWire {
  artifact_id?: string;
  display_session_id?: string;
  profile_slug?: string;
  session_ids?: string[];
  session_starts_at?: string[];
  title?: string;
  updated_at?: string;
}

export interface MomentsSessionAttachmentSummaryWire {
  attachment_id?: string;
  kind?: string;
  drive_file_id?: string;
  display_name?: string;
  status?: string;
  content_status?: string;
}

export interface MomentsSessionReadinessSummaryWire {
  status?: string;
  version?: number;
  reason?: string;
  input_set_hash?: string;
}

export interface MomentsSessionRowWire {
  session_id?: string;
  legacy_session_id?: string;
  planned_start_at?: string;
  planned_end_at?: string;
  status?: string;
  artifacts?: MomentsSessionArtifactSummaryWire[] | null;
  attachments?: MomentsSessionAttachmentSummaryWire[] | null;
  artifact_generation_readiness?: MomentsSessionReadinessSummaryWire | null;
  failed_artifact_profiles?: string[] | null;
}

interface MomentsSessionReadWireResponse {
  item?: MomentsSessionRowWire | null;
}

export interface MomentsFeatureFlagSubjectWire {
  person_id?: string;
  roles?: string[] | null;
}

export interface MomentsFeatureFlagsForSelfWireResponse {
  subject?: MomentsFeatureFlagSubjectWire | null;
  flags?: string[] | null;
  source?: string;
}

export interface MomentsFeatureFlagForSubjectWireResponse {
  subject?: MomentsFeatureFlagSubjectWire | null;
  flag_key?: string;
  enabled?: boolean;
  source?: string;
}

interface MomentsSessionLinkSummaryWire {
  session_id?: string;
  legacy_session_id?: string;
  planned_start_at?: string;
  planned_end_at?: string;
  coach_name?: string;
}

interface MomentsArtifactSourceWire {
  type?: string;
  value?: string;
  label?: string | null;
}

interface MomentsArtifactTranscriptSessionWire {
  transcript_id?: string;
  session_start_at?: string | null;
  /** Moments session id. */
  session_id?: string | null;
  /** Rails session id. */
  legacy_session_id?: string | null;
  session_type?: string | null;
}

interface MomentsArtifactDetailWireResponse {
  id?: string;
  owner_person_id?: string;
  owner_display_name?: string | null;
  subject_id?: string;
  profile_slug?: string;
  title?: string;
  sources?: MomentsArtifactSourceWire[] | null;
  markdown?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  revision?: number;
  transcript_sessions?: MomentsArtifactTranscriptSessionWire[] | null;
  session_prep?: Record<string, unknown> | null;
}

interface MomentsArtifactFeedbackWire {
  id?: string;
  feedback_type?: string;
  feedback_text?: string | null;
  created_at?: string;
}

interface MomentsArtifactFeedbackLatestWireResponse {
  feedback?: MomentsArtifactFeedbackWire | null;
}

interface MomentsArtifactFeedbackCreateWireResponse {
  id?: string;
  person_id?: string;
  feedback_type?: string;
  feedback_text?: string | null;
  context?: Record<string, unknown>;
  created_at?: string;
}

type MomentsReflectionSourceIdentityWire = {
  revision: number;
  block_id: string;
  start_index: number;
  end_index: number;
} & (
  | {
      reader_contract: 'frontend-reflection-legacy-v1';
      anchor_contract: 'reflection-keep-anchor-v1';
      scope: 'whole-block' | 'selection';
    }
  | {
      reader_contract: 'reflection-v2-item-v1';
      anchor_contract: 'reflection-v2-key-v1';
      scope: 'whole-block';
    }
);

interface MomentsArtifactNoteWire {
  id?: string;
  owner_person_id?: string;
  artifact_id?: string;
  selected_text?: string;
  block_id?: string;
  start_index?: number;
  end_index?: number;
  note?: string;
  tags?: string[] | null;
  suggested_tag?: string | null;
  author_role?: string;
  session_ref_type?: string | null;
  session_ref_id?: string | null;
  created_at?: string;
  updated_at?: string;
  source_identity?: MomentsReflectionSourceIdentityWire | null;
}

interface MomentsArtifactNotesWireResponse {
  notes?: MomentsArtifactNoteWire[] | null;
}

interface MomentsSavedHighlightArtifactWire {
  title?: string;
  profile_slug?: string;
  updated_at?: string;
  sessions?: MomentsSessionLinkSummaryWire[] | null;
  route_date_slug?: string;
}

interface MomentsSavedHighlightWire extends MomentsArtifactNoteWire {
  artifact?: MomentsSavedHighlightArtifactWire | null;
}

interface MomentsSavedHighlightsSearchWireResponse {
  items?: MomentsSavedHighlightWire[] | null;
  total_count?: number;
  has_more?: boolean;
  next_offset?: number;
}

interface TrackMomentsAnalyticsEventWireResponse {
  queued?: boolean;
}

interface MomentsMemberArtifactSummaryWire {
  artifact_id?: string;
  owner_person_id?: string;
  subject_id?: string;
  profile_slug?: string;
  title?: string;
  updated_at?: string;
  revision?: number;
  sessions?: MomentsSessionLinkSummaryWire[] | null;
  markdown?: string;
  content_markdown?: string;
  session_prep?: Record<string, unknown> | null;
}

interface MomentsMemberArtifactsWireResponse {
  items?: MomentsMemberArtifactSummaryWire[] | null;
  total_count?: number;
  has_more?: boolean;
  next_offset?: number;
}

interface MomentsSessionArtifactsWireResponse {
  session?: MomentsSessionLinkSummaryWire | null;
  items?: MomentsMemberArtifactSummaryWire[] | null;
}

interface MomentsSessionTranscriptSummaryWire {
  attachment_id?: string;
  transcript_content_id?: string;
  drive_file_id?: string;
  display_name?: string;
  content_status?: string;
  content_markdown?: string;
  content_fetched_at?: string;
  drive_modified_at?: string;
  content_length?: number;
}

interface MomentsSessionTranscriptsWireResponse {
  session?: MomentsSessionLinkSummaryWire | null;
  items?: MomentsSessionTranscriptSummaryWire[] | null;
}

interface MomentsBearerQueryArgs<T> {
  body?: unknown;
  fallbackData: T;
  mapResponse: (payload: unknown) => T;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  returnErrorOnFailure?: boolean;
}

export interface ResolveMomentsBaseUrlArgs {
  envUrl?: string;
  fallbackUrl?: string | null;
}

export interface MomentsQueryError {
  data?: unknown;
  status: number | string;
}

export const resolveMomentsBaseUrl = ({
  envUrl,
  fallbackUrl,
}: ResolveMomentsBaseUrlArgs): string | null => {
  const candidate = envUrl?.trim() || fallbackUrl?.trim();
  if (!candidate) return null;

  try {
    const url = new URL(candidate.replace(/\/+$/u, ''));
    const isLocal =
      url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (
      (url.protocol !== 'https:' && !(isLocal && url.protocol === 'http:')) ||
      url.username ||
      url.password ||
      (url.pathname !== '/' && url.pathname !== '') ||
      url.search ||
      url.hash
    ) {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
};

const getEnvMomentsBaseUrl = (): string | undefined =>
  (import.meta.env as RuntimeImportMetaEnv).VITE_MOMENTS_BASE_URL;

export const getCachedMomentsBaseUrlFallback = (
  state: unknown,
): string | null => {
  const apiState = (state as RootLikeState | undefined)?.api;
  const queries = apiState?.queries;
  if (!queries) {
    return null;
  }

  for (const queryState of Object.values(queries)) {
    const currentUser = queryState.data as CurrentUserLike | undefined;
    if (typeof currentUser?.momentsBaseUrl === 'string') {
      return currentUser.momentsBaseUrl;
    }
  }

  return null;
};

const resolveMomentsBaseUrlForQuery = (queryApi: BaseQueryApi): string | null =>
  resolveMomentsBaseUrl({
    envUrl: getEnvMomentsBaseUrl(),
    fallbackUrl: getCachedMomentsBaseUrlFallback(queryApi.getState()),
  });

const buildMomentsUrl = (baseUrl: string, path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

const resolveMomentsTokenCacheKey = (
  baseUrl: string,
  readiness: MomentsApiSessionReadiness,
): string | null => {
  const personId = readiness.person?.id.trim();
  if (!personId) {
    return null;
  }

  return `${baseUrl}::${personId}`;
};

const parseJsonSafely = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const mapMomentsApiSessionReadiness = (
  payload: MomentsApiSessionReadinessWire,
): MomentsApiSessionReadiness => ({
  capabilities: {
    coachTeam: payload.capabilities?.coach_team === true,
    memberApi: payload.capabilities?.member_api === true,
  },
  person: payload.person
    ? {
        email: payload.person.email ?? '',
        id: payload.person.id ?? '',
        organizationId: payload.person.organization_id ?? '',
      }
    : undefined,
  preconditionsMet: payload.preconditions_met === true,
  provisioned: payload.provisioned === true,
  ready: payload.ready === true,
  reason: payload.reason,
  roles: payload.roles ?? [],
});

export const momentsPreflightQuery = async (
  queryApi: BaseQueryApi,
): Promise<
  { data: MomentsApiSessionReadiness } | { error: MomentsQueryError }
> => {
  const isCancelled = () => queryApi.signal.aborted;
  let baseUrl = resolveMomentsBaseUrlForQuery(queryApi);
  if (!baseUrl && !getEnvMomentsBaseUrl() && !isCancelled()) {
    // A reset can remove the cached fallback while mounted asset subscriptions
    // restart. Resolve the genuine viewer once, rather than caching empty assets.
    const { userApi } = await import('./userApi');
    if (isCancelled()) return { error: { status: 'CANCELLED' } };
    const lookup = queryApi.dispatch(
      userApi.endpoints.getCurrentUser.initiate(),
    );
    try {
      await lookup;
      if (isCancelled()) return { error: { status: 'CANCELLED' } };
      baseUrl = resolveMomentsBaseUrlForQuery(queryApi);
    } finally {
      lookup.unsubscribe();
    }
  }
  if (isCancelled()) return { error: { status: 'CANCELLED' } };
  if (!baseUrl) {
    return {
      error: {
        data: 'VITE_MOMENTS_BASE_URL is not configured',
        status: 'MOMENTS_BASE_URL_MISSING',
      },
    };
  }

  const generation = getMomentsGeneration(queryApi.getState());
  try {
    const response = await fetch(
      buildMomentsUrl(baseUrl, '/api/v1/auth/api-session-readiness'),
      {
        credentials: 'include',
        headers: { Accept: 'application/json' },
        method: 'GET',
        signal: queryApi.signal,
      },
    );
    const payload = await parseJsonSafely(response);
    if (isCancelled()) return { error: { status: 'CANCELLED' } };

    if (
      generation !== getMomentsGeneration(queryApi.getState()) ||
      baseUrl !== resolveMomentsBaseUrlForQuery(queryApi)
    ) {
      return { error: { status: 'MOMENTS_PRIVATE_BOUNDARY' } };
    }
    if (!response.ok) {
      if (response.status === 401) {
        queryApi.dispatch(failMomentsReadiness());
      }
      return {
        error: {
          data: payload,
          status: response.status,
        },
      };
    }
    const readiness = mapMomentsApiSessionReadiness(
      payload as MomentsApiSessionReadinessWire,
    );
    queryApi.dispatch(restoreMomentsReadiness());
    observeMomentsReadinessIdentity(queryApi, readiness, baseUrl);
    return { data: readiness };
  } catch (error) {
    return {
      error: {
        data: error instanceof Error ? error.message : error,
        status: 'FETCH_ERROR',
      },
    };
  }
};

const defaultMomentsAccessTokenTtlMs = 2 * 60 * 60 * 1000;

const resolveTokenExpiryMs = (
  payload: MomentsTokenExchangeResponse,
): number => {
  if (payload.expires_at) {
    const parsed = Date.parse(payload.expires_at);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  if (typeof payload.expires_in === 'number' && payload.expires_in > 0) {
    return Date.now() + payload.expires_in * 1000;
  }

  return Date.now() + defaultMomentsAccessTokenTtlMs;
};

export class MomentsTokenClient {
  private accessToken: string | null = null;

  private expiresAtMs = 0;

  private tokenCacheKey: string | null = null;

  private exchangeInFlight: Promise<string | null> | null = null;

  private exchangeInFlightKey: string | null = null;

  private cacheGeneration = 0;

  private readonly expirySkewMs = 60_000;

  clear(): void {
    this.cacheGeneration += 1;
    this.accessToken = null;
    this.expiresAtMs = 0;
    this.tokenCacheKey = null;
    this.exchangeInFlight = null;
    this.exchangeInFlightKey = null;
  }

  getCachedAccessTokenForTesting(): string | null {
    return this.accessToken;
  }

  async getAccessToken({
    baseUrl,
    readiness,
  }: {
    baseUrl: string;
    readiness: MomentsApiSessionReadiness | undefined;
  }): Promise<string | null> {
    if (!readiness?.ready) {
      return null;
    }

    const cacheKey = resolveMomentsTokenCacheKey(baseUrl, readiness);
    if (!cacheKey) {
      this.clear();
      return null;
    }

    if (this.hasUsableAccessToken(cacheKey)) {
      return this.accessToken;
    }

    if (this.exchangeInFlight && this.exchangeInFlightKey === cacheKey) {
      return this.exchangeInFlight;
    }

    const exchangeGeneration = this.cacheGeneration;
    const exchangePromise = this.exchangeForAccessToken(
      baseUrl,
      cacheKey,
      exchangeGeneration,
    );
    this.exchangeInFlight = exchangePromise;
    this.exchangeInFlightKey = cacheKey;

    try {
      return await exchangePromise;
    } finally {
      if (this.exchangeInFlight === exchangePromise) {
        this.exchangeInFlight = null;
        this.exchangeInFlightKey = null;
      }
    }
  }

  private hasUsableAccessToken(cacheKey: string): boolean {
    return Boolean(
      this.accessToken &&
      this.tokenCacheKey === cacheKey &&
      this.expiresAtMs - this.expirySkewMs > Date.now(),
    );
  }

  private async exchangeForAccessToken(
    baseUrl: string,
    cacheKey: string,
    exchangeGeneration: number,
  ): Promise<string | null> {
    try {
      const response = await fetch(
        buildMomentsUrl(baseUrl, '/api/v1/auth/token/exchange'),
        {
          body: JSON.stringify({
            client: 'rails-frontend',
            requested_audience: ['identity'],
            requested_scopes: ['read', 'write'],
          }),
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          method: 'POST',
        },
      );

      const payload = (await parseJsonSafely(
        response,
      )) as MomentsTokenExchangeResponse;

      if (exchangeGeneration !== this.cacheGeneration) {
        return null;
      }

      if (!response.ok || !payload.access_token) {
        this.clear();
        return null;
      }

      this.accessToken = payload.access_token;
      this.expiresAtMs = resolveTokenExpiryMs(payload);
      this.tokenCacheKey = cacheKey;
      return this.accessToken;
    } catch {
      if (exchangeGeneration === this.cacheGeneration) {
        this.clear();
      }
      return null;
    }
  }
}

export const momentsTokenClient = new MomentsTokenClient();

const momentsBearerPerformanceClearHook =
  '__clearMomentsBearerTokenForPerformanceTesting' as const;

if (
  typeof window !== 'undefined' &&
  window.location.hostname === 'staging.mento.co'
) {
  (
    window as Window & {
      [momentsBearerPerformanceClearHook]?: () => void;
    }
  )[momentsBearerPerformanceClearHook] = () => momentsTokenClient.clear();
}

const createMomentsAnalyticsSessionClient = () => {
  const personNotFoundRetryCooldownMs = 5_000;
  let token: string | null = null;
  let expiresAt = '';
  let expiresAtMs = 0;
  let personId: string | null = null;
  let personNotFoundRetryAfterMs = 0;
  let generation = 0;
  let sessionInFlight: Promise<MomentsAnalyticsSessionResponse | null> | null =
    null;
  // True once the cached token was acquired while the caller already knew the
  // final customer_type (or definitively knew none applies). A token acquired
  // before that was known must not be treated as final, or a page's very
  // first tracked event permanently locks the session out of ever labeling
  // the Mixpanel profile.
  let capturedWithResolvedCustomerType = false;

  const clear = (): void => {
    token = null;
    expiresAt = '';
    expiresAtMs = 0;
    personId = null;
    personNotFoundRetryAfterMs = 0;
    capturedWithResolvedCustomerType = false;
    generation += 1;
    sessionInFlight = null;
  };

  // Usable regardless of whether the customer_type label was ever attached,
  // for falling back to during the post-404 cooldown instead of returning
  // null and dropping an otherwise-valid session.
  const getUsableCachedSession = (): MomentsAnalyticsSessionResponse | null =>
    token && personId && expiresAtMs > Date.now() + 60_000
      ? { analyticsToken: token, expiresAt, personId }
      : null;

  const getSession = async (
    baseUrl: string,
    customerType?: 'b2b' | 'd2c',
    customerTypeResolved = true,
  ): Promise<MomentsAnalyticsSessionResponse | null> => {
    if (
      token &&
      personId &&
      expiresAtMs > Date.now() + 60_000 &&
      (capturedWithResolvedCustomerType || !customerTypeResolved)
    )
      return {
        analyticsToken: token,
        expiresAt,
        personId,
      };
    if (Date.now() < personNotFoundRetryAfterMs)
      return getUsableCachedSession();
    if (!sessionInFlight) {
      const requestGeneration = generation;
      sessionInFlight = fetch(buildMomentsUrl(baseUrl, '/api/v1/pa/session'), {
        body: JSON.stringify(
          customerType ? { customer_type: customerType } : {},
        ),
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
        .then(async (response) => {
          if (response.status === 404) {
            if (requestGeneration !== generation) return null;
            personNotFoundRetryAfterMs =
              Date.now() + personNotFoundRetryCooldownMs;
            return getUsableCachedSession();
          }
          if (!response.ok) return null;
          const payload = (await parseJsonSafely(response)) as {
            analytics_token?: string;
            expires_at?: string;
            person_id?: string;
          };
          if (!payload.analytics_token || !payload.person_id) return null;
          if (requestGeneration !== generation) return null;
          token = payload.analytics_token;
          expiresAt = payload.expires_at ?? '';
          expiresAtMs = Date.parse(expiresAt);
          personId = payload.person_id;
          personNotFoundRetryAfterMs = 0;
          capturedWithResolvedCustomerType = customerTypeResolved;
          return {
            analyticsToken: payload.analytics_token,
            expiresAt,
            personId: payload.person_id,
          };
        })
        .catch(() => null)
        .finally(() => {
          if (requestGeneration === generation) sessionInFlight = null;
        });
    }
    return sessionInFlight;
  };
  return { clear, getSession };
};

export const momentsAnalyticsSessionClient =
  createMomentsAnalyticsSessionClient();

interface AnalyticsCustomerType {
  value: 'b2b' | 'd2c' | undefined;
  // False while the current-user lookup this value depends on has not
  // finished yet, so callers can avoid permanently caching a session that
  // was acquired before the real answer was known.
  resolved: boolean;
}

const analyticsCustomerType = (state: unknown): AnalyticsCustomerType => {
  const queries = (state as RootLikeState | undefined)?.api?.queries ?? {};
  for (const [key, query] of Object.entries(queries)) {
    if (key.startsWith('getCurrentUser(')) {
      const user = query.data as
        | {
            isBusinessPaid?: boolean;
            id?: string;
            roles?: Array<string | { name?: string }>;
          }
        | undefined;
      if (
        typeof user?.id === 'string' &&
        typeof user.isBusinessPaid === 'boolean'
      ) {
        if (
          user.roles?.some((role) => {
            const name = typeof role === 'string' ? role : role.name;
            return name === 'coach' || name === 'admin';
          })
        )
          return { resolved: true, value: undefined };
        return { resolved: true, value: user.isBusinessPaid ? 'b2b' : 'd2c' };
      }
      return { resolved: query.status === 'rejected', value: undefined };
    }
  }
  return { resolved: false, value: undefined };
};

const postMomentsAnalyticsEvent = async (
  baseUrl: string,
  session: MomentsAnalyticsSessionResponse,
  arg: TrackMomentsAnalyticsEventRequest,
): Promise<Response> =>
  fetch(buildMomentsUrl(baseUrl, '/api/v1/pa/events'), {
    body: JSON.stringify({
      client_event_id: arg.clientEventId,
      event_name: arg.eventName,
      occurred_at: arg.occurredAt,
      properties: arg.properties,
      query_params: arg.queryParams,
    }),
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      Authorization: `Analytics ${session.analyticsToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

const momentsAnalyticsQuery = async (
  queryApi: BaseQueryApi,
  arg: TrackMomentsAnalyticsEventRequest,
): Promise<
  { data: TrackMomentsAnalyticsEventResponse } | { error: MomentsQueryError }
> => {
  const baseUrl = resolveMomentsBaseUrlForQuery(queryApi);
  if (!baseUrl) return { data: { queued: false } };
  const { value: customerType, resolved: customerTypeResolved } =
    analyticsCustomerType(queryApi.getState());
  let session = await momentsAnalyticsSessionClient.getSession(
    baseUrl,
    customerType,
    customerTypeResolved,
  );
  if (!session) return { data: { queued: false } };
  try {
    let response = await postMomentsAnalyticsEvent(baseUrl, session, arg);
    if (response.status === 401) {
      momentsAnalyticsSessionClient.clear();
      session = await momentsAnalyticsSessionClient.getSession(
        baseUrl,
        customerType,
        customerTypeResolved,
      );
      if (!session) return { data: { queued: false } };
      response = await postMomentsAnalyticsEvent(baseUrl, session, arg);
    }
    if (!response.ok) return { data: { queued: false } };
    return {
      data: {
        queued:
          (
            (await parseJsonSafely(
              response,
            )) as TrackMomentsAnalyticsEventWireResponse
          ).queued === true,
      },
    };
  } catch {
    return { data: { queued: false } };
  }
};

const fallbackCoachMembersResponse: MomentsCoachMembersResponse = {
  members: [],
};

const fallbackFeatureFlagsForSelfResponse: MomentsFeatureFlagsForSelfResponse =
  {
    flags: [],
    source: 'moments',
    subject: { personId: '', roles: [] },
  };

const fallbackFeatureFlagForSubjectResponse = ({
  flagKey,
  personId = '',
}: {
  flagKey: string;
  personId?: string;
}): MomentsFeatureFlagForSubjectResponse => ({
  enabled: false,
  flagKey,
  source: 'moments',
  subject: { personId, roles: [] },
});

const fallbackMemberArtifactsResponse: MomentsMemberArtifactsResponse = {
  hasMore: false,
  items: [],
  totalCount: 0,
};

const fallbackSessionArtifactsResponse: MomentsSessionArtifactsResponse = {
  items: [],
  session: { sessionId: '' },
};

const fallbackSessionTranscriptsResponse: MomentsSessionTranscriptsResponse = {
  items: [],
  session: { sessionId: '' },
};

const fallbackSessionReadResponse: MomentsSessionReadResponse = {
  item: null,
};

export const mapMomentsFeatureFlagsForSelfResponse = (
  payload: MomentsFeatureFlagsForSelfWireResponse,
): MomentsFeatureFlagsForSelfResponse => ({
  flags: payload.flags ?? [],
  source: 'moments',
  subject: {
    personId: payload.subject?.person_id ?? '',
    roles: payload.subject?.roles ?? [],
  },
});

export const mapMomentsFeatureFlagForSubjectResponse = (
  payload: MomentsFeatureFlagForSubjectWireResponse,
  fallback: { flagKey: string; personId?: string },
): MomentsFeatureFlagForSubjectResponse => ({
  enabled: payload.enabled === true,
  flagKey: payload.flag_key ?? fallback.flagKey,
  source: 'moments',
  subject: {
    personId: payload.subject?.person_id ?? fallback.personId ?? '',
    roles: payload.subject?.roles ?? [],
  },
});

const mapMomentsSessionLinkSummary = (
  payload: MomentsSessionLinkSummaryWire | null | undefined,
): MomentsSessionLinkSummary => ({
  coachName: payload?.coach_name,
  legacySessionId: payload?.legacy_session_id,
  plannedEndAt: payload?.planned_end_at,
  plannedStartAt: payload?.planned_start_at,
  sessionId: payload?.session_id ?? '',
});

const mapMomentsArtifactSource = (
  payload: MomentsArtifactSourceWire,
): MomentsArtifactSource => ({
  label: payload.label,
  type: payload.type ?? '',
  value: payload.value ?? '',
});

const mapMomentsArtifactTranscriptSession = (
  payload: MomentsArtifactTranscriptSessionWire,
): MomentsArtifactTranscriptSession => ({
  legacySessionId: payload.legacy_session_id,
  sessionId: payload.session_id,
  sessionStartAt: payload.session_start_at,
  sessionType: payload.session_type,
  transcriptId: payload.transcript_id ?? '',
});

const mapMomentsSessionPrepSourceReflection = (
  payload: unknown,
): MomentsSessionPrepSourceReflection | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const value = payload as Record<string, unknown>;
  return {
    artifactId:
      typeof value.artifact_id === 'string' ? value.artifact_id : undefined,
    routeDateSlug:
      typeof value.route_date_slug === 'string'
        ? value.route_date_slug
        : undefined,
    title: typeof value.title === 'string' ? value.title : undefined,
  };
};

const mapMomentsSessionPrepContext = (
  payload: Record<string, unknown> | null | undefined,
): MomentsSessionPrepContext | undefined => {
  if (!payload) {
    return undefined;
  }
  return {
    ...payload,
    sourceInputSetHash:
      typeof payload.source_input_set_hash === 'string'
        ? payload.source_input_set_hash
        : undefined,
    sourceLegacySessionId:
      typeof payload.source_legacy_session_id === 'string'
        ? payload.source_legacy_session_id
        : undefined,
    sourcePlannedEndAt:
      typeof payload.source_planned_end_at === 'string' ||
      payload.source_planned_end_at === null
        ? payload.source_planned_end_at
        : undefined,
    sourcePlannedStartAt:
      typeof payload.source_planned_start_at === 'string' ||
      payload.source_planned_start_at === null
        ? payload.source_planned_start_at
        : undefined,
    sourceReadinessVersion:
      typeof payload.source_readiness_version === 'number'
        ? payload.source_readiness_version
        : undefined,
    sourceReflection: mapMomentsSessionPrepSourceReflection(
      payload.source_reflection,
    ),
    sourceSessionId:
      typeof payload.source_session_id === 'string'
        ? payload.source_session_id
        : undefined,
    sourceUsedCoachMatch:
      typeof payload.source_used_coach_match === 'boolean'
        ? payload.source_used_coach_match
        : undefined,
    targetLegacySessionId:
      typeof payload.target_legacy_session_id === 'string'
        ? payload.target_legacy_session_id
        : undefined,
    targetPlannedEndAt:
      typeof payload.target_planned_end_at === 'string' ||
      payload.target_planned_end_at === null
        ? payload.target_planned_end_at
        : undefined,
    targetPlannedStartAt:
      typeof payload.target_planned_start_at === 'string' ||
      payload.target_planned_start_at === null
        ? payload.target_planned_start_at
        : undefined,
    targetSessionId:
      typeof payload.target_session_id === 'string'
        ? payload.target_session_id
        : undefined,
  };
};

export const mapMomentsArtifactDetail = (
  payload: MomentsArtifactDetailWireResponse,
): MomentsArtifactDetail => ({
  createdAt: payload.created_at ?? '',
  createdBy: payload.created_by ?? '',
  id: payload.id ?? '',
  markdown: payload.markdown ?? '',
  ownerDisplayName: payload.owner_display_name,
  ownerPersonId: payload.owner_person_id ?? '',
  profileSlug: payload.profile_slug ?? '',
  revision: payload.revision ?? 0,
  sessionPrep: mapMomentsSessionPrepContext(payload.session_prep),
  sources: (payload.sources ?? []).map(mapMomentsArtifactSource),
  subjectId: payload.subject_id,
  title: payload.title ?? '',
  transcriptSessions: (payload.transcript_sessions ?? []).map(
    mapMomentsArtifactTranscriptSession,
  ),
  updatedAt: payload.updated_at ?? '',
  updatedBy: payload.updated_by ?? '',
});

const normalizeMomentsArtifactFeedbackType = (
  value: string | undefined,
): MomentsArtifactFeedbackType | null => {
  if (value === 'positive' || value === 'negative') {
    return value;
  }
  return null;
};

const mapMomentsArtifactFeedback = (
  payload: MomentsArtifactFeedbackWire,
): MomentsArtifactFeedback | null => {
  const feedbackType = normalizeMomentsArtifactFeedbackType(
    payload.feedback_type,
  );
  if (!feedbackType) {
    return null;
  }
  return {
    createdAt: payload.created_at ?? '',
    feedbackText: payload.feedback_text,
    feedbackType,
    id: payload.id ?? '',
  };
};

export const mapMomentsArtifactFeedbackLatestResponse = (
  payload: MomentsArtifactFeedbackLatestWireResponse,
): MomentsArtifactFeedbackLatestResponse => ({
  feedback: payload.feedback
    ? mapMomentsArtifactFeedback(payload.feedback)
    : null,
});

export const mapMomentsArtifactFeedbackCreateResponse = (
  payload: MomentsArtifactFeedbackCreateWireResponse,
): MomentsArtifactFeedbackCreateResponse | null => {
  const feedbackType = normalizeMomentsArtifactFeedbackType(
    payload.feedback_type,
  );
  if (!feedbackType) {
    return null;
  }
  return {
    context: payload.context,
    createdAt: payload.created_at ?? '',
    feedbackText: payload.feedback_text,
    feedbackType,
    id: payload.id ?? '',
    personId: payload.person_id ?? '',
  };
};

const mapStoredSourceIdentity = (
  wire: MomentsReflectionSourceIdentityWire,
):
  | MomentsReflectionStoredSourceIdentity
  | MomentsReflectionV2StoredSourceIdentity => {
  const placement = {
    blockId: wire.block_id,
    endIndex: wire.end_index,
    revision: wire.revision,
    startIndex: wire.start_index,
  };
  return wire.reader_contract === 'reflection-v2-item-v1'
    ? {
        ...placement,
        anchorContract: wire.anchor_contract,
        readerContract: wire.reader_contract,
        scope: wire.scope,
      }
    : {
        ...placement,
        anchorContract: wire.anchor_contract,
        readerContract: wire.reader_contract,
        scope: wire.scope,
      };
};

export const mapMomentsArtifactNote = (
  payload: MomentsArtifactNoteWire,
): MomentsArtifactNote => ({
  artifactId: payload.artifact_id ?? '',
  authorRole: payload.author_role ?? '',
  blockId: payload.block_id ?? '',
  createdAt: payload.created_at ?? '',
  endIndex: payload.end_index ?? 0,
  id: payload.id ?? '',
  note: payload.note ?? '',
  ownerPersonId: payload.owner_person_id ?? '',
  selectedText: payload.selected_text ?? '',
  ...(payload.source_identity !== undefined
    ? {
        sourceIdentity: payload.source_identity
          ? mapStoredSourceIdentity(payload.source_identity)
          : null,
      }
    : {}),
  sessionRefId: payload.session_ref_id,
  sessionRefType: payload.session_ref_type,
  startIndex: payload.start_index ?? 0,
  suggestedTag: payload.suggested_tag,
  tags: payload.tags ?? [],
  updatedAt: payload.updated_at ?? '',
});

export const mapMomentsArtifactNotesResponse = (
  payload: MomentsArtifactNotesWireResponse,
): MomentsArtifactNotesResponse => ({
  notes: (payload.notes ?? []).map(mapMomentsArtifactNote),
});

export const mapMomentsSavedHighlightArtifactSummary = (
  payload: MomentsSavedHighlightArtifactWire | null | undefined,
): MomentsSavedHighlightArtifactSummary => ({
  profileSlug: payload?.profile_slug ?? '',
  routeDateSlug: payload?.route_date_slug ?? '',
  sessions: (payload?.sessions ?? []).map(mapMomentsSessionLinkSummary),
  title: payload?.title ?? '',
  updatedAt: payload?.updated_at ?? '',
});

export const mapMomentsSavedHighlight = (
  payload: MomentsSavedHighlightWire,
): MomentsSavedHighlight => ({
  ...mapMomentsArtifactNote(payload),
  artifact: payload.artifact
    ? mapMomentsSavedHighlightArtifactSummary(payload.artifact)
    : null,
});

export const mapMomentsSavedHighlightsSearchResponse = (
  payload: MomentsSavedHighlightsSearchWireResponse,
): MomentsSavedHighlightsSearchResponse => ({
  hasMore: payload.has_more === true,
  items: (payload.items ?? []).map(mapMomentsSavedHighlight),
  nextOffset: payload.next_offset,
  totalCount: payload.total_count ?? 0,
});

const getMomentsMemberArtifactSummaryMarkdown = (
  payload: MomentsMemberArtifactSummaryWire,
): string | undefined => {
  if (payload.markdown?.trim()) {
    return payload.markdown;
  }

  return payload.content_markdown;
};

const mapMomentsMemberArtifactSummary = (
  payload: MomentsMemberArtifactSummaryWire,
): MomentsMemberArtifactSummary => ({
  artifactId: payload.artifact_id ?? '',
  markdown: getMomentsMemberArtifactSummaryMarkdown(payload),
  ownerPersonId: payload.owner_person_id ?? '',
  profileSlug: payload.profile_slug ?? '',
  revision: payload.revision ?? 0,
  sessionPrep: mapMomentsSessionPrepContext(payload.session_prep),
  sessions: (payload.sessions ?? []).map(mapMomentsSessionLinkSummary),
  subjectId: payload.subject_id ?? '',
  title: payload.title ?? '',
  updatedAt: payload.updated_at ?? '',
});

export const mapMomentsMemberArtifactsResponse = (
  payload: MomentsMemberArtifactsWireResponse,
): MomentsMemberArtifactsResponse => ({
  hasMore: payload.has_more === true,
  items: (payload.items ?? []).map(mapMomentsMemberArtifactSummary),
  nextOffset: payload.next_offset,
  totalCount: payload.total_count ?? 0,
});

export const mapMomentsSessionArtifactsResponse = (
  payload: MomentsSessionArtifactsWireResponse,
): MomentsSessionArtifactsResponse => ({
  items: (payload.items ?? []).map(mapMomentsMemberArtifactSummary),
  session: mapMomentsSessionLinkSummary(payload.session),
});

const mapMomentsCoachSessionArtifactsResponse = (payload: {
  item?: MomentsSessionRowWire | null;
}): MomentsSessionArtifactsResponse => {
  const session = payload.item;
  return {
    items: (session?.artifacts ?? []).map((artifact) => ({
      artifactId: artifact.artifact_id ?? '',
      ownerPersonId: '',
      profileSlug: artifact.profile_slug ?? '',
      revision: 0,
      sessions: [
        {
          legacySessionId: session?.legacy_session_id,
          plannedEndAt: session?.planned_end_at,
          plannedStartAt: session?.planned_start_at,
          sessionId: session?.session_id ?? artifact.display_session_id ?? '',
        },
      ],
      subjectId: '',
      title: artifact.title ?? '',
      updatedAt: artifact.updated_at ?? '',
    })),
    session: mapMomentsSessionLinkSummary(session),
  };
};

const mapMomentsSessionTranscriptSummary = (
  payload: MomentsSessionTranscriptSummaryWire,
): MomentsSessionTranscriptSummary => ({
  attachmentId: payload.attachment_id ?? '',
  contentFetchedAt: payload.content_fetched_at,
  contentLength: payload.content_length,
  contentMarkdown: payload.content_markdown,
  contentStatus: payload.content_status ?? '',
  displayName: payload.display_name,
  driveFileId: payload.drive_file_id ?? '',
  driveModifiedAt: payload.drive_modified_at,
  transcriptContentId: payload.transcript_content_id,
});

export const mapMomentsSessionTranscriptsResponse = (
  payload: MomentsSessionTranscriptsWireResponse,
): MomentsSessionTranscriptsResponse => ({
  items: (payload.items ?? []).map(mapMomentsSessionTranscriptSummary),
  session: mapMomentsSessionLinkSummary(payload.session),
});

const mapMomentsSessionReadAttachmentSummary = (
  payload: MomentsSessionAttachmentSummaryWire,
): MomentsSessionReadAttachmentSummary => ({
  attachmentId: payload.attachment_id ?? '',
  contentStatus: payload.content_status ?? '',
  displayName: payload.display_name,
  driveFileId: payload.drive_file_id ?? '',
  kind: payload.kind ?? '',
  status: payload.status ?? '',
});

const mapMomentsSessionReadinessSummary = (
  payload: MomentsSessionReadinessSummaryWire | null | undefined,
): MomentsSessionReadinessSummary | undefined => {
  if (!payload) {
    return undefined;
  }
  return {
    inputSetHash: payload.input_set_hash,
    reason: payload.reason ?? '',
    status: payload.status ?? '',
    version: payload.version ?? 0,
  };
};

const mapMomentsSessionReadArtifactSummary = (
  payload: MomentsSessionArtifactSummaryWire,
): MomentsSessionReadArtifactSummary => ({
  artifactId: payload.artifact_id ?? '',
  displaySessionId: payload.display_session_id ?? '',
  profileSlug: payload.profile_slug ?? '',
  sessionIds: payload.session_ids ?? [],
  sessionStartsAt: payload.session_starts_at ?? [],
  title: payload.title ?? '',
  updatedAt: payload.updated_at ?? '',
});

const mapMomentsSessionReadItem = (
  payload: MomentsSessionRowWire,
): MomentsSessionReadItem => ({
  ...mapMomentsSessionLinkSummary(payload),
  artifactGenerationReadiness: mapMomentsSessionReadinessSummary(
    payload.artifact_generation_readiness,
  ),
  artifacts: (payload.artifacts ?? []).map(
    mapMomentsSessionReadArtifactSummary,
  ),
  attachments: (payload.attachments ?? []).map(
    mapMomentsSessionReadAttachmentSummary,
  ),
  failedArtifactProfiles: payload.failed_artifact_profiles ?? [],
  status: payload.status ?? '',
});

export const mapMomentsSessionReadResponse = (
  payload: MomentsSessionReadWireResponse,
): MomentsSessionReadResponse => ({
  item: payload.item ? mapMomentsSessionReadItem(payload.item) : null,
});

const buildQueryString = (params: Record<string, string>): string =>
  new URLSearchParams(params).toString();

const serializeMomentsQueryValue = (
  value: string | readonly string[] | number | boolean,
): string => (Array.isArray(value) ? value.join(',') : String(value));

const buildOptionalQueryString = (
  params: Record<
    string,
    string | readonly string[] | number | boolean | undefined
  >,
): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.set(key, serializeMomentsQueryValue(value));
    }
  }
  return searchParams.toString();
};

export const mapMomentsCoachMembersResponse = (
  payload: MomentsCoachMembersWireResponse,
): MomentsCoachMembersResponse => ({
  members: (payload.members ?? [])
    .map((member): MomentsCoachMember | null => {
      const { person } = member;
      const personId = person?.id?.trim();
      const email = (person?.primary_email ?? person?.email ?? '').trim();

      if (!personId || !email) {
        return null;
      }

      const givenName = person?.given_name?.trim() ?? '';
      const familyName = person?.family_name?.trim() ?? '';
      const fullName = [givenName, familyName].filter(Boolean).join(' ').trim();

      return {
        active: member.active === true,
        email,
        fullName: fullName || email,
        personId,
        provisioned: true,
        relationshipType: member.relationship_type ?? null,
        source: member.source,
      };
    })
    .filter((member): member is MomentsCoachMember => member !== null),
});

export const findMomentsMemberByEmail = (
  members: MomentsCoachMember[] | undefined,
  email: string | undefined,
): MomentsCoachMember | null => {
  const normalizedEmail = email?.trim().toLowerCase();
  if (!normalizedEmail) {
    return null;
  }

  return (
    members?.find(
      (member) =>
        member.provisioned !== false &&
        member.active !== false &&
        member.email.trim().toLowerCase() === normalizedEmail,
    ) ?? null
  );
};

const buildMomentsArtifactPath = ({
  artifactId,
  revision,
}: GetMomentsArtifactArg): string | null => {
  const trimmedArtifactId = artifactId.trim();
  if (!trimmedArtifactId) {
    return null;
  }
  const query = buildOptionalQueryString({
    id: trimmedArtifactId,
    revision,
  });
  return `/rpc/v1/artifact.get?${query}`;
};

const buildMomentsArtifactFeedbackLatestPath = ({
  artifactId,
}: GetMomentsArtifactFeedbackLatestArg): string | null => {
  const trimmedArtifactId = artifactId.trim();
  if (!trimmedArtifactId) {
    return null;
  }
  return `/rpc/v1/artifact/feedback.latest?${buildQueryString({
    artifact_id: trimmedArtifactId,
  })}`;
};

const buildMomentsArtifactFeedbackCreateBody = ({
  artifactId,
  feedbackText,
  feedbackType,
  surface = 'member_reflection_detail',
}: CreateMomentsArtifactFeedbackArg) => ({
  context: {
    artifact_id: artifactId.trim(),
    surface,
  },
  feedback_text: feedbackText,
  feedback_type: feedbackType,
});

const buildMomentsArtifactNotesListPath = ({
  artifactId,
}: ListMomentsArtifactNotesArg = {}): string => {
  const trimmedArtifactId = artifactId?.trim();
  if (!trimmedArtifactId) {
    return '/rpc/v1/artifact_note.list';
  }
  return `/rpc/v1/artifact_note.list?${buildQueryString({
    artifact_id: trimmedArtifactId,
  })}`;
};

const buildMomentsSavedHighlightsSearchPath = ({
  direction,
  limit,
  noteId,
  offset,
  profileSlug,
  q,
  sort,
  view,
}: SearchMomentsSavedHighlightsArg = {}): string => {
  const query = buildOptionalQueryString({
    direction,
    limit,
    note_id: noteId,
    offset,
    profile_slug: profileSlug,
    q: q?.trim() || undefined,
    sort,
    view,
  });
  return `/rpc/v1/artifact_note.searchMine${query ? `?${query}` : ''}`;
};

const buildMomentsArtifactNoteCreateBody = ({
  artifactId,
  authorRole,
  blockId,
  endIndex,
  note,
  selectedText,
  sessionRefId,
  sessionRefType,
  sourceIdentity,
  startIndex,
  suggestedTag,
  tags,
}: CreateMomentsArtifactNoteArg) => ({
  artifact_id: artifactId.trim(),
  author_role: authorRole,
  block_id: blockId,
  end_index: endIndex,
  note,
  selected_text: selectedText,
  session_ref_id: sessionRefId,
  session_ref_type: sessionRefType,
  source_identity: sourceIdentity
    ? {
        anchor_contract: sourceIdentity.anchorContract,
        reader_contract: sourceIdentity.readerContract,
        revision: sourceIdentity.revision,
        scope: sourceIdentity.scope,
      }
    : undefined,
  start_index: startIndex,
  suggested_tag: suggestedTag,
  tags,
});

const buildMomentsArtifactNoteUpdateBody = ({
  clearSuggestedTag,
  id,
  note,
  suggestedTag,
  tags,
}: UpdateMomentsArtifactNoteArg) => ({
  clear_suggested_tag: clearSuggestedTag,
  id: id.trim(),
  note,
  suggested_tag: suggestedTag,
  tags,
});

const buildMomentsSessionArtifactsPath = ({
  includeContent,
  legacySessionId,
  profileSlug,
  sessionId,
}: GetMomentsSessionScopedArg): string | null => {
  if (!sessionId && !legacySessionId) {
    return null;
  }
  if (sessionId && legacySessionId) {
    return null;
  }
  const query = buildOptionalQueryString({
    include_content: includeContent,
    legacy_session_id: legacySessionId,
    profile_slug: profileSlug,
    session_id: sessionId,
  });
  return `/rpc/v1/artifact.listBySession?${query}`;
};

const buildMomentsSessionTranscriptsPath = ({
  includeContent,
  legacySessionId,
  sessionId,
}: GetMomentsSessionTranscriptsArg): string | null => {
  if (!sessionId && !legacySessionId) {
    return null;
  }
  if (sessionId && legacySessionId) {
    return null;
  }
  const query = buildOptionalQueryString({
    include_content: includeContent,
  });
  const suffix = query ? `?${query}` : '';
  if (legacySessionId) {
    return `/api/v1/sessions/by-legacy-id/${encodeURIComponent(
      legacySessionId,
    )}/transcripts${suffix}`;
  }
  return `/api/v1/sessions/${encodeURIComponent(
    sessionId ?? '',
  )}/transcripts${suffix}`;
};

type MomentsBearerAttemptResult<T> =
  { data: T } | { error: MomentsQueryError } | { retryAuth: true };

export const momentsBearerQuery = async <T>(
  queryApi: BaseQueryApi,
  {
    body,
    fallbackData,
    mapResponse,
    method = 'GET',
    path,
    returnErrorOnFailure = false,
  }: MomentsBearerQueryArgs<T>,
): Promise<{ data: T } | { error: MomentsQueryError }> => {
  const generation = getMomentsGeneration(queryApi.getState());
  const boundaryError = (): { error: MomentsQueryError } => ({
    error: { status: 'MOMENTS_PRIVATE_BOUNDARY' },
  });
  const crossedBoundary = () =>
    generation !== getMomentsGeneration(queryApi.getState());
  const readinessResult = await momentsPreflightQuery(queryApi);
  if (crossedBoundary()) {
    return boundaryError();
  }
  if ('error' in readinessResult) {
    if (returnErrorOnFailure || readinessResult.error.status === 'CANCELLED') {
      return { error: readinessResult.error };
    }
    return { data: fallbackData };
  }

  const readiness = readinessResult.data;
  if (!readiness.ready) {
    if (returnErrorOnFailure) {
      return { error: { data: readiness, status: 'MOMENTS_NOT_READY' } };
    }
    return { data: fallbackData };
  }

  const baseUrl = resolveMomentsBaseUrlForQuery(queryApi);
  if (!baseUrl) {
    if (returnErrorOnFailure) {
      return {
        error: {
          data: 'VITE_MOMENTS_BASE_URL is not configured',
          status: 'MOMENTS_BASE_URL_MISSING',
        },
      };
    }
    return { data: fallbackData };
  }

  const isCancelled = () => queryApi.signal.aborted;
  const requestWithToken = async (): Promise<MomentsBearerAttemptResult<T>> => {
    if (isCancelled()) return { error: { status: 'CANCELLED' } };
    const accessToken = await momentsTokenClient.getAccessToken({
      baseUrl,
      readiness,
    });
    if (isCancelled()) return { error: { status: 'CANCELLED' } };
    if (crossedBoundary()) {
      return boundaryError();
    }
    if (!accessToken) {
      if (returnErrorOnFailure) {
        return { error: { status: 'MOMENTS_TOKEN_MISSING' } };
      }
      return { data: fallbackData };
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(buildMomentsUrl(baseUrl, path), {
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
      headers,
      method,
      signal: queryApi.signal,
    });
    const payload = await parseJsonSafely(response);
    // A late old-session rejection must not clear the new session's token.
    if (isCancelled()) return { error: { status: 'CANCELLED' } };
    if (crossedBoundary()) {
      return boundaryError();
    }

    if (response.status === 401) {
      momentsTokenClient.clear();
      return { retryAuth: true };
    }

    if (response.status === 428) {
      momentsTokenClient.clear();
      if (returnErrorOnFailure) {
        return { error: { data: payload, status: response.status } };
      }
      return { data: fallbackData };
    }

    if (!response.ok) {
      if (returnErrorOnFailure) {
        return { error: { data: payload, status: response.status } };
      }
      return { data: fallbackData };
    }

    return { data: mapResponse(payload) };
  };

  try {
    const firstResult = await requestWithToken();
    if ('retryAuth' in firstResult) {
      if (crossedBoundary()) {
        return boundaryError();
      }
      const secondResult = await requestWithToken();
      if ('retryAuth' in secondResult) {
        queryApi.dispatch(clearMomentsPrivateState());
        if (returnErrorOnFailure) {
          return { error: { status: 401 } };
        }
        return { data: fallbackData };
      }
      return secondResult;
    }
    return firstResult;
  } catch {
    if (isCancelled()) return { error: { status: 'CANCELLED' } };
    if (returnErrorOnFailure) {
      return { error: { status: 'FETCH_ERROR' } };
    }
    return { data: fallbackData };
  }
};

export const momentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    recordMomentsArtifactSurveyAnswer: builder.mutation<
      MomentsArtifactSurveyAnswerResult | null,
      RecordMomentsArtifactSurveyAnswerArg
    >({
      invalidatesTags: (_result, error) =>
        error ? [] : ['MomentsArtifactSurvey'],
      queryFn: async (arg, queryApi) =>
        momentsBearerQuery(queryApi, {
          body: {
            answer: arg.answer,
            artifact_id: arg.artifactId.trim(),
            question: arg.question,
          },
          fallbackData: null,
          mapResponse: (payload) => {
            // A backend that predates the IDs answers 204 with no body; the
            // answer is still saved, so omit the IDs rather than fail it.
            if (!payload) {
              return null;
            }
            const result = payload as {
              response_id: string;
              survey_id: string;
            };
            return {
              responseId: result.response_id,
              surveyId: result.survey_id,
            };
          },
          method: 'POST',
          path: '/rpc/v1/artifact/survey.answer',
          returnErrorOnFailure: true,
        }),
    }),

    createMomentsArtifactFeedback: builder.mutation<
      MomentsArtifactFeedbackCreateResponse | null,
      CreateMomentsArtifactFeedbackArg
    >({
      invalidatesTags: (result, _error, arg) =>
        result ? [{ id: arg.artifactId, type: 'MomentsArtifactFeedback' }] : [],
      queryFn: async (arg, queryApi) => {
        const trimmedArtifactId = arg.artifactId.trim();
        if (!trimmedArtifactId) {
          return { data: null };
        }
        return momentsBearerQuery(queryApi, {
          body: buildMomentsArtifactFeedbackCreateBody({
            ...arg,
            artifactId: trimmedArtifactId,
          }),
          fallbackData: null,
          mapResponse: (payload) =>
            mapMomentsArtifactFeedbackCreateResponse(
              payload as MomentsArtifactFeedbackCreateWireResponse,
            ),
          method: 'POST',
          path: '/rpc/v1/artifact/feedback.create',
          returnErrorOnFailure: true,
        });
      },
    }),

    getMomentsArtifactSurveyStatus: builder.query<
      MomentsArtifactSurveyStatus,
      void
    >({
      providesTags: ['MomentsArtifactSurvey'],
      queryFn: async (_arg, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: { answered: true },
          mapResponse: (payload) => ({
            answered: Boolean(
              (payload as { answered?: boolean } | undefined)?.answered,
            ),
          }),
          path: '/rpc/v1/artifact/survey.status',
          returnErrorOnFailure: true,
        }),
    }),

    getMomentsApiSessionReadiness: builder.query<
      MomentsApiSessionReadiness,
      void
    >({
      providesTags: ['MomentsApiSession'],
      queryFn: async (_arg, queryApi) => momentsPreflightQuery(queryApi),
    }),

    getMomentsArtifact: builder.query<
      MomentsArtifactDetail | null,
      GetMomentsArtifactArg
    >({
      queryFn: async (arg, queryApi) => {
        const path = buildMomentsArtifactPath(arg);
        if (!path) {
          return { data: null };
        }
        return momentsBearerQuery(queryApi, {
          fallbackData: null,
          mapResponse: (payload) =>
            mapMomentsArtifactDetail(
              payload as MomentsArtifactDetailWireResponse,
            ),
          path,
          returnErrorOnFailure: true,
        });
      },
    }),

    getMomentsArtifactFeedbackLatest: builder.query<
      MomentsArtifactFeedbackLatestResponse,
      GetMomentsArtifactFeedbackLatestArg
    >({
      providesTags: (_result, _error, arg) => [
        { id: arg.artifactId, type: 'MomentsArtifactFeedback' },
      ],
      queryFn: async (arg, queryApi) => {
        const path = buildMomentsArtifactFeedbackLatestPath(arg);
        if (!path) {
          return { data: { feedback: null } };
        }
        return momentsBearerQuery(queryApi, {
          fallbackData: { feedback: null },
          mapResponse: (payload) =>
            mapMomentsArtifactFeedbackLatestResponse(
              payload as MomentsArtifactFeedbackLatestWireResponse,
            ),
          path,
          returnErrorOnFailure: true,
        });
      },
    }),

    listMomentsArtifactNotes: builder.query<
      MomentsArtifactNotesResponse,
      ListMomentsArtifactNotesArg | void
    >({
      providesTags: (result, _error, arg) => {
        const artifactId = arg?.artifactId?.trim() || 'ALL';
        return result
          ? [
              ...result.notes.map((note) => ({
                id: note.id,
                type: 'MomentsArtifactNotes' as const,
              })),
              { id: artifactId, type: 'MomentsArtifactNotes' as const },
            ]
          : [{ id: artifactId, type: 'MomentsArtifactNotes' as const }];
      },
      queryFn: async (arg, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: { notes: [] },
          mapResponse: (payload) =>
            mapMomentsArtifactNotesResponse(
              payload as MomentsArtifactNotesWireResponse,
            ),
          path: buildMomentsArtifactNotesListPath(arg || {}),
          returnErrorOnFailure: true,
        }),
    }),

    searchMomentsSavedHighlights: builder.query<
      MomentsSavedHighlightsSearchResponse,
      SearchMomentsSavedHighlightsArg | void
    >({
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((highlight) => ({
                id: highlight.id,
                type: 'MomentsArtifactNotes' as const,
              })),
              { id: 'SAVED_HIGHLIGHTS', type: 'MomentsArtifactNotes' as const },
            ]
          : [
              {
                id: 'SAVED_HIGHLIGHTS',
                type: 'MomentsArtifactNotes' as const,
              },
            ],
      queryFn: async (arg, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: { hasMore: false, items: [], totalCount: 0 },
          mapResponse: (payload) =>
            mapMomentsSavedHighlightsSearchResponse(
              payload as MomentsSavedHighlightsSearchWireResponse,
            ),
          path: buildMomentsSavedHighlightsSearchPath(arg || {}),
          returnErrorOnFailure: true,
        }),
    }),

    createMomentsArtifactNote: builder.mutation<
      MomentsArtifactNote | null,
      CreateMomentsArtifactNoteArg
    >({
      invalidatesTags: (result, _error, arg) =>
        result
          ? [
              { id: arg.artifactId.trim(), type: 'MomentsArtifactNotes' },
              { id: 'ALL', type: 'MomentsArtifactNotes' },
              { id: 'SAVED_HIGHLIGHTS', type: 'MomentsArtifactNotes' },
            ]
          : [],
      queryFn: async (arg, queryApi) => {
        const trimmedArtifactId = arg.artifactId.trim();
        if (!trimmedArtifactId) {
          return { data: null };
        }
        return momentsBearerQuery(queryApi, {
          body: buildMomentsArtifactNoteCreateBody({
            ...arg,
            artifactId: trimmedArtifactId,
          }),
          fallbackData: null,
          mapResponse: (payload) =>
            mapMomentsArtifactNote(payload as MomentsArtifactNoteWire),
          method: 'POST',
          path: '/rpc/v1/artifact_note.create',
          returnErrorOnFailure: true,
        });
      },
    }),

    updateMomentsArtifactNote: builder.mutation<
      MomentsArtifactNote | null,
      UpdateMomentsArtifactNoteArg
    >({
      invalidatesTags: [{ type: 'MomentsArtifactNotes' }],
      queryFn: async (arg, queryApi) => {
        if (!arg.id.trim()) {
          return { data: null };
        }
        return momentsBearerQuery(queryApi, {
          body: buildMomentsArtifactNoteUpdateBody(arg),
          fallbackData: null,
          mapResponse: (payload) =>
            mapMomentsArtifactNote(payload as MomentsArtifactNoteWire),
          method: 'POST',
          path: '/rpc/v1/artifact_note.update',
          returnErrorOnFailure: true,
        });
      },
    }),

    deleteMomentsArtifactNote: builder.mutation<
      void,
      DeleteMomentsArtifactNoteArg
    >({
      invalidatesTags: [{ type: 'MomentsArtifactNotes' }],
      queryFn: async (arg, queryApi) => {
        if (!arg.id.trim()) {
          return { data: undefined };
        }
        return momentsBearerQuery(queryApi, {
          body: { id: arg.id.trim() },
          fallbackData: undefined,
          mapResponse: () => undefined,
          method: 'POST',
          path: '/rpc/v1/artifact_note.delete',
          returnErrorOnFailure: true,
        });
      },
    }),

    trackMomentsAnalyticsEvent: builder.mutation<
      TrackMomentsAnalyticsEventResponse,
      TrackMomentsAnalyticsEventRequest
    >({
      queryFn: async (arg, queryApi) => {
        if (!arg.eventName.trim() || !arg.clientEventId.trim()) {
          return { data: { queued: false } };
        }
        return momentsAnalyticsQuery(queryApi, arg);
      },
    }),

    getMomentsCoachMembers: builder.query<MomentsCoachMembersResponse, void>({
      providesTags: ['MomentsCoachMembers'],
      queryFn: async (_arg, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: fallbackCoachMembersResponse,
          mapResponse: (payload) =>
            mapMomentsCoachMembersResponse(
              payload as MomentsCoachMembersWireResponse,
            ),
          path: '/api/v1/coach/team',
        }),
    }),

    getMomentsCoachSessionArtifacts: builder.query<
      MomentsSessionArtifactsResponse,
      GetMomentsCoachSessionArtifactsArg
    >({
      queryFn: async ({ legacySessionId, memberPersonId }, queryApi) => {
        const query = buildOptionalQueryString({
          include_unresolved: true,
          person_id: memberPersonId,
          role: 'coachee',
        });
        return momentsBearerQuery(queryApi, {
          fallbackData: fallbackSessionArtifactsResponse,
          mapResponse: (payload) =>
            mapMomentsCoachSessionArtifactsResponse(
              payload as { item?: MomentsSessionRowWire | null },
            ),
          path: `/api/v1/sessions/by-legacy-id/${encodeURIComponent(
            legacySessionId,
          )}?${query}`,
        });
      },
    }),

    getMomentsSessionReadByLegacyId: builder.query<
      MomentsSessionReadResponse,
      GetMomentsSessionReadByLegacyIdArg
    >({
      queryFn: async ({ legacySessionId }, queryApi) => {
        const trimmedLegacySessionId = legacySessionId.trim();
        if (!trimmedLegacySessionId) {
          return { data: fallbackSessionReadResponse };
        }
        return momentsBearerQuery(queryApi, {
          fallbackData: fallbackSessionReadResponse,
          mapResponse: (payload) =>
            mapMomentsSessionReadResponse(
              payload as MomentsSessionReadWireResponse,
            ),
          path: `/api/v1/sessions/by-legacy-id/${encodeURIComponent(
            trimmedLegacySessionId,
          )}`,
          returnErrorOnFailure: true,
        });
      },
    }),

    getMomentsFeatureFlagForLegacySubject: builder.query<
      MomentsFeatureFlagForSubjectResponse,
      GetMomentsFeatureFlagForLegacySubjectArg
    >({
      providesTags: ['MomentsFeatureFlags'],
      queryFn: async ({ externalId, flagKey }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: fallbackFeatureFlagForSubjectResponse({ flagKey }),
          mapResponse: (payload) =>
            mapMomentsFeatureFlagForSubjectResponse(
              payload as MomentsFeatureFlagForSubjectWireResponse,
              { flagKey },
            ),
          path: `/api/v1/feature-flags/subjects/by-identity?${buildQueryString({
            external_id: externalId,
            flag_key: flagKey,
            provider: 'legacy_app',
          })}`,
          returnErrorOnFailure: true,
        }),
    }),

    getMomentsFeatureFlagForSubject: builder.query<
      MomentsFeatureFlagForSubjectResponse,
      GetMomentsFeatureFlagForSubjectArg
    >({
      providesTags: ['MomentsFeatureFlags'],
      queryFn: async ({ flagKey, personId }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: fallbackFeatureFlagForSubjectResponse({
            flagKey,
            personId,
          }),
          mapResponse: (payload) =>
            mapMomentsFeatureFlagForSubjectResponse(
              payload as MomentsFeatureFlagForSubjectWireResponse,
              { flagKey, personId },
            ),
          path: `/api/v1/feature-flags/subjects/${encodeURIComponent(
            personId,
          )}?${buildQueryString({ flag_key: flagKey })}`,
          returnErrorOnFailure: true,
        }),
    }),

    getMomentsFeatureFlagsForSelf: builder.query<
      MomentsFeatureFlagsForSelfResponse,
      void
    >({
      providesTags: ['MomentsFeatureFlags'],
      queryFn: async (_arg, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: fallbackFeatureFlagsForSelfResponse,
          mapResponse: (payload) =>
            mapMomentsFeatureFlagsForSelfResponse(
              payload as MomentsFeatureFlagsForSelfWireResponse,
            ),
          path: '/api/v1/feature-flags/self',
          returnErrorOnFailure: true,
        }),
    }),

    getMomentsMemberArtifacts: builder.query<
      MomentsMemberArtifactsResponse,
      GetMomentsMemberArtifactsArg | void
    >({
      queryFn: async (arg, queryApi) => {
        const params = arg || {};
        const query = buildOptionalQueryString({
          include_content: params.includeContent,
          limit: params.limit,
          offset: params.offset,
          profile_slug: params.profileSlug,
        });
        return momentsBearerQuery(queryApi, {
          fallbackData: fallbackMemberArtifactsResponse,
          mapResponse: (payload) =>
            mapMomentsMemberArtifactsResponse(
              payload as MomentsMemberArtifactsWireResponse,
            ),
          path: `/rpc/v1/artifact.listMine${query ? `?${query}` : ''}`,
        });
      },
    }),

    getMomentsSessionArtifacts: builder.query<
      MomentsSessionArtifactsResponse,
      GetMomentsSessionScopedArg
    >({
      queryFn: async (arg, queryApi) => {
        const path = buildMomentsSessionArtifactsPath(arg);
        if (!path) {
          return { data: fallbackSessionArtifactsResponse };
        }
        return momentsBearerQuery(queryApi, {
          fallbackData: fallbackSessionArtifactsResponse,
          mapResponse: (payload) =>
            mapMomentsSessionArtifactsResponse(
              payload as MomentsSessionArtifactsWireResponse,
            ),
          path,
        });
      },
    }),

    getMomentsMemberScopeAppointments: builder.query<
      MomentsMemberScopeAppointmentsPage,
      GetMomentsMemberScopeAppointmentsArg
    >({
      providesTags: (_result, _error, { memberId }) => [
        { id: `MEMBER_SCOPE:${memberId}`, type: 'Appointment' },
      ],
      queryFn: async (
        { memberId, limit = 100, order, rangeEnd, rangeStart, readMode },
        queryApi,
      ) => {
        const appointments: AdminAppointmentPage['appointments'] = [];
        try {
          const readinessResult = await momentsPreflightQuery(queryApi);
          if ('error' in readinessResult) {
            if (
              readMode !== 'history' &&
              isMomentsMemberScopeEligibilityError(readinessResult.error)
            ) {
              return { data: unavailableMomentsMemberScopeAppointmentsPage };
            }
            return { error: readinessResult.error };
          }
          if (!readinessResult.data.ready) {
            if (readMode === 'history') {
              return {
                error: {
                  data: readinessResult.data.reason,
                  status: 'MOMENTS_NOT_READY',
                },
              };
            }
            return { data: unavailableMomentsMemberScopeAppointmentsPage };
          }
          const baseUrl = resolveMomentsBaseUrlForQuery(queryApi);
          if (!baseUrl) {
            if (readMode === 'history') {
              return { error: { status: 'MOMENTS_BASE_URL_MISSING' } };
            }
            return { data: unavailableMomentsMemberScopeAppointmentsPage };
          }

          const seenCursors = new Set<string>();
          let cursor: string | null = null;
          let pageRequests = 0;
          let effectiveCoachCount: number | undefined;
          do {
            if (queryApi.signal.aborted) {
              throw new Error('Member-scope Appointment pagination aborted');
            }
            pageRequests += 1;
            const params = new URLSearchParams({
              limit: String(limit),
              member_external_id: memberId,
              member_provider: 'legacy_app',
            });
            if (order) params.set('order', order);
            if (rangeEnd) params.set('range_end', rangeEnd);
            if (rangeStart) params.set('range_start', rangeStart);
            if (cursor) params.set('cursor', cursor);
            if (readMode) params.set('read_mode', readMode);
            let result:
              | { data: unknown }
              | { error: MomentsQueryError }
              | { retryAuth: true } = { retryAuth: true };
            for (
              let attempt = 0;
              attempt < 2 && 'retryAuth' in result;
              attempt += 1
            ) {
              // eslint-disable-next-line no-await-in-loop -- retry may need a freshly exchanged token for this page
              const accessToken = await momentsTokenClient.getAccessToken({
                baseUrl,
                readiness: readinessResult.data,
              });
              if (!accessToken) {
                result = { error: { status: 'MOMENTS_TOKEN_MISSING' } };
                break;
              }
              // eslint-disable-next-line no-await-in-loop -- sequential pagination requires the previous page cursor
              const response = await fetch(
                buildMomentsUrl(
                  baseUrl,
                  `/api/v1/appointments/member-scope?${params.toString()}`,
                ),
                {
                  credentials: 'include',
                  headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                  },
                  method: 'GET',
                  ...(readMode === 'history'
                    ? { signal: queryApi.signal }
                    : {}),
                },
              );
              // eslint-disable-next-line no-await-in-loop -- response body belongs to this sequential page request
              const payload = await parseJsonSafely(response);
              if (response.status === 401) {
                momentsTokenClient.clear();
                result = { retryAuth: true };
              } else if (response.status === 428) {
                momentsTokenClient.clear();
                result = { error: { data: payload, status: response.status } };
              } else if (!response.ok) {
                result = { error: { data: payload, status: response.status } };
              } else {
                result = { data: payload };
              }
            }
            if ('retryAuth' in result) result = { error: { status: 401 } };
            if ('error' in result) {
              if (
                readMode !== 'history' &&
                pageRequests === 1 &&
                isMomentsMemberScopeEligibilityError(result.error)
              ) {
                return { data: unavailableMomentsMemberScopeAppointmentsPage };
              }
              return { error: result.error };
            }
            if (!result.data) throw new Error('Missing member-scope page');
            const page = appointmentContractV1.adminAppointmentPage.decode(
              result.data,
            );
            if (pageRequests === 1) {
              effectiveCoachCount = page.effectiveCoachCount;
            }
            if (
              readMode !== 'history' &&
              pageRequests === 1 &&
              page.scopeEmpty === true &&
              page.appointments.length === 0
            ) {
              return {
                data: {
                  ...unavailableMomentsMemberScopeAppointmentsPage,
                  effectiveCoachCount: page.effectiveCoachCount,
                  scopeEmpty: true,
                },
              };
            }
            appointments.push(...page.appointments);
            if (page.nextCursor && seenCursors.has(page.nextCursor)) {
              throw new Error(
                'Member-scope Appointment pagination cursor repeated',
              );
            }
            if (page.nextCursor) seenCursors.add(page.nextCursor);
            cursor = page.nextCursor;
          } while (
            cursor &&
            pageRequests < MAX_MOMENTS_MEMBER_SCOPE_PAGE_REQUESTS
          );
          if (cursor) {
            throw new Error(
              'Member-scope Appointment pagination exceeded request limit',
            );
          }
          return {
            data: {
              appointments: highestVersionAppointments(appointments),
              contractVersion: 'appointment-admin-page.v1',
              effectiveCoachCount,
              nextCursor: null,
            },
          };
        } catch (error: unknown) {
          return {
            error: {
              data: error instanceof Error ? error.message : String(error),
              status: 'CUSTOM_ERROR',
            },
          };
        }
      },
    }),

    getMomentsSessionTranscripts: builder.query<
      MomentsSessionTranscriptsResponse,
      GetMomentsSessionTranscriptsArg
    >({
      queryFn: async (arg, queryApi) => {
        const path = buildMomentsSessionTranscriptsPath(arg);
        if (!path) {
          return { data: fallbackSessionTranscriptsResponse };
        }
        return momentsBearerQuery(queryApi, {
          fallbackData: fallbackSessionTranscriptsResponse,
          mapResponse: (payload) =>
            mapMomentsSessionTranscriptsResponse(
              payload as MomentsSessionTranscriptsWireResponse,
            ),
          path,
          returnErrorOnFailure: true,
        });
      },
    }),
  }),
});

export const {
  useCreateMomentsArtifactFeedbackMutation,
  useCreateMomentsArtifactNoteMutation,
  useDeleteMomentsArtifactNoteMutation,
  useGetMomentsApiSessionReadinessQuery,
  useGetMomentsArtifactFeedbackLatestQuery,
  useGetMomentsArtifactQuery,
  useGetMomentsArtifactSurveyStatusQuery,
  useListMomentsArtifactNotesQuery,
  useRecordMomentsArtifactSurveyAnswerMutation,
  useSearchMomentsSavedHighlightsQuery,
  useTrackMomentsAnalyticsEventMutation,
  useGetMomentsCoachMembersQuery,
  useGetMomentsCoachSessionArtifactsQuery,
  useGetMomentsFeatureFlagForLegacySubjectQuery,
  useGetMomentsFeatureFlagForSubjectQuery,
  useGetMomentsFeatureFlagsForSelfQuery,
  useGetMomentsMemberArtifactsQuery,
  useGetMomentsMemberScopeAppointmentsQuery,
  useGetMomentsSessionArtifactsQuery,
  useGetMomentsSessionReadByLegacyIdQuery,
  useGetMomentsSessionTranscriptsQuery,
  useUpdateMomentsArtifactNoteMutation,
} = momentsApi;
